<script setup lang="ts">
/**
 * How far a running job has come: a thin rounded bar filled to `value` (0–1).
 * Without a value (`null`) it runs indeterminate — motion instead of a made-up
 * percentage — until the total is known. `showValue` prints the percentage
 * beside the bar.
 *
 * Progress only; for actual against a target (hours booked of planned) use
 * NxProgressMeter. Exposed as a `progressbar` named by `label`.
 */
import { computed, inject } from 'vue';
import { i18nInjectionKey } from '../internal';

const props = withDefaults(
  defineProps<{
    /** Share done, 0–1; `null` for indeterminate. */
    value?: number | null;
    tone?: 'brand' | 'success' | 'warning' | 'danger' | 'neutral';
    size?: 'sm' | 'md' | 'lg';
    /** Accessible name („Synchronisierung“). */
    label?: string;
    showValue?: boolean;
    locale?: string;
  }>(),
  {
    value: null,
    tone: 'brand',
    size: 'md',
    label: 'Fortschritt',
    showValue: false,
    locale: undefined,
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);

const determinate = computed(() => typeof props.value === 'number' && Number.isFinite(props.value));
const share = computed(() =>
  determinate.value ? Math.max(0, Math.min(1, props.value as number)) : 0
);
const valueText = computed(() =>
  determinate.value
    ? new Intl.NumberFormat(locale.value, { style: 'percent', maximumFractionDigits: 0 }).format(
        share.value
      )
    : ''
);
</script>

<template>
  <div
    class="nx-progress-bar"
    :class="[`tone-${props.tone}`, `size-${props.size}`, { 'is-indeterminate': !determinate }]"
    data-testid="nx-progress-bar"
  >
    <div
      class="nx-progress-bar__track"
      role="progressbar"
      :aria-label="props.label"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="determinate ? Math.round(share * 100) : undefined"
      :aria-valuetext="determinate ? valueText : undefined"
    >
      <span
        class="nx-progress-bar__fill"
        :style="determinate ? { width: `${share * 100}%` } : undefined"
      />
    </div>
    <span v-if="props.showValue && determinate" class="nx-progress-bar__value" aria-hidden="true">
      {{ valueText }}
    </span>
  </div>
</template>

<style scoped>
.nx-progress-bar {
  --bar-height: calc(var(--nxt-size-unit) * 0.375);
  --bar-fill: var(--nxt-brand-bg);

  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  width: 100%;
  min-width: 0;
}
.nx-progress-bar.size-sm {
  --bar-height: calc(var(--nxt-size-unit) * 0.25);
}
.nx-progress-bar.size-lg {
  --bar-height: calc(var(--nxt-size-unit) * 0.625);
}
.nx-progress-bar.tone-success {
  --bar-fill: var(--nxt-success-bg);
}
.nx-progress-bar.tone-warning {
  --bar-fill: var(--nxt-warning-bg);
}
.nx-progress-bar.tone-danger {
  --bar-fill: var(--nxt-danger-bg);
}
.nx-progress-bar.tone-neutral {
  --bar-fill: var(--nxt-fg-muted);
}
.nx-progress-bar__track {
  position: relative;
  flex: 1 1 auto;
  height: var(--bar-height);
  overflow: hidden;
  border-radius: var(--nxt-radius-full);
  background: color-mix(in srgb, var(--nxt-fg) 8%, transparent);
}
.nx-progress-bar__fill {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--bar-fill) 75%, var(--nxt-surface-bg)),
    var(--bar-fill)
  );
  transition: width var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-progress-bar.is-indeterminate .nx-progress-bar__fill {
  width: 35%;
  animation: nx-progress-slide 1.4s var(--nxt-ease-in-out) infinite;
}
.nx-progress-bar__value {
  flex: 0 0 auto;
  min-width: 4ch;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  font-weight: var(--nxt-font-weight-semibold);
  text-align: end;
}
@keyframes nx-progress-slide {
  from {
    transform: translateX(-110%);
  }
  to {
    transform: translateX(300%);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-progress-bar__fill {
    transition: none;
  }
  .nx-progress-bar.is-indeterminate .nx-progress-bar__fill {
    width: 100%;
    opacity: 0.35;
    animation: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-progress-bar__fill {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-progress-bar.is-indeterminate .nx-progress-bar__fill {
  width: 100%;
  opacity: 0.35;
  animation: none;
}

@media (forced-colors: active) {
  .nx-progress-bar__fill {
    background: Highlight;
  }
}
</style>
